import { Tooltip as BaseTooltip } from '@base-ui/react/tooltip';
import type { ReactElement, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import { popupPlacement } from './popupPlacement';

export type TooltipPlacement =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-start'
  | 'top-end'
  | 'bottom-start'
  | 'bottom-end'
  | 'left-start'
  | 'left-end'
  | 'right-start'
  | 'right-end';

export interface TooltipProps {
  title: ReactNode;
  children: ReactElement;
  placement?: TooltipPlacement;
  /** Delay before showing, in ms. */
  delay?: number;
  /** MUI alias for `delay`. */
  enterDelay?: number;
  /** Delay before hiding, in ms. */
  leaveDelay?: number;
  arrow?: boolean;
  open?: boolean;
  onClose?: () => void;
  disableHoverListener?: boolean;
  /** Extra classes on the popup (merged after the default tooltip styles). */
  className?: string;
}

export default function Tooltip({
  title,
  children,
  placement = 'top',
  delay,
  enterDelay,
  leaveDelay,
  arrow = false,
  open,
  onClose,
  disableHoverListener = false,
  className
}: TooltipProps) {
  if (disableHoverListener || title == null || title === false || title === '') {
    return children;
  }

  const { side, align } = popupPlacement(placement);

  return (
    <BaseTooltip.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose?.();
      }}
    >
      <BaseTooltip.Trigger delay={delay ?? enterDelay} closeDelay={leaveDelay} render={children} />
      <BaseTooltip.Portal>
        {/* Fixed + above RightPanel (z-1001). Stay below the critical/toast-adjacent
            9999 layer so hover copy does not cover the navbar bell or toasts. */}
        <BaseTooltip.Positioner
          side={side}
          align={align}
          sideOffset={8}
          positionMethod="fixed"
          collisionPadding={8}
          className="z-[1100]"
        >
          <BaseTooltip.Popup
            role="tooltip"
            className={twMerge(
              'z-[1100] max-w-xs rounded bg-gray-700 px-2 py-1 text-xs text-white shadow-md',
              className
            )}
          >
            {title}
            {arrow && <BaseTooltip.Arrow className="fill-gray-700" />}
          </BaseTooltip.Popup>
        </BaseTooltip.Positioner>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  );
}
